import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';

const FLUID_TYPES = [
  '0.9% Sodium Chloride',
  '0.9% Sodium Chloride with 5% Glucose',
  '0.18% Sodium Chloride with 4% Glucose',
  '5% Glucose',
  "Hartmann's Solution",
  'Sodium Bicarbonate 1.26%',
  'Sodium Bicarbonate 8.4%',
  'Gelofusine',
  'Albumin 4%',
  'Albumin 20%',
  'Packed Red Blood Cells',
  'Water for Injection',
];

const ADDITIVE_OPTIONS = [
  'Potassium Chloride 10mmol/L',
  'Potassium Chloride 20mmol/L',
  'Potassium Chloride 40mmol/L',
  'Magnesium Sulphate 10mmol',
  'Magnesium Sulphate 20mmol',
  'Sodium Bicarbonate 50mmol',
];

const createFluidId = () => `fluid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const formatIsoDateForInput = (date) => date.toISOString().slice(0, 10);
const formatTimeForInput = (date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const computeDuration = (volumeMl, rateMlPerHour) => {
  const v = Number(volumeMl);
  const r = Number(rateMlPerHour);
  if (!v || !r || r <= 0) return null;
  return Math.round((v / r) * 10) / 10;
};

const computeRate = (volumeMl, durationHours) => {
  const v = Number(volumeMl);
  const d = Number(durationHours);
  if (!v || !d || d <= 0) return null;
  return Math.round((v / d) * 10) / 10;
};

export default function AddFluidPrescription({ show, onHide, onSave, prescriberName = 'Student user', editFluid = null }) {
  const [fluidType, setFluidType] = useState('');
  const [customFluidType, setCustomFluidType] = useState('');
  const [volumeMl, setVolumeMl] = useState('');
  const [rateMlPerHour, setRateMlPerHour] = useState('');
  const [durationHours, setDurationHours] = useState('');
  const [rateMode, setRateMode] = useState('rate');
  const [additives, setAdditives] = useState([]);
  const [customAdditive, setCustomAdditive] = useState('');
  const [indication, setIndication] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;
    if (editFluid) {
      const knownType = FLUID_TYPES.includes(editFluid.drug);
      setFluidType(knownType ? editFluid.drug : 'Other');
      setCustomFluidType(knownType ? '' : editFluid.drug);
      setVolumeMl(String(editFluid.volumeMl || ''));
      setRateMlPerHour(String(editFluid.rateMlPerHour || ''));
      setDurationHours(String(editFluid.durationHours || ''));
      setRateMode('rate');
      setAdditives(Array.isArray(editFluid.additives) ? editFluid.additives : []);
      setCustomAdditive('');
      setIndication(editFluid.indication || '');
      const parsedStart = editFluid.start_date ? new Date(editFluid.start_date) : new Date();
      setStartDate(formatIsoDateForInput(parsedStart));
      setStartTime(formatTimeForInput(parsedStart));
    } else {
      const now = new Date();
      setFluidType('');
      setCustomFluidType('');
      setVolumeMl('');
      setRateMlPerHour('');
      setDurationHours('');
      setRateMode('rate');
      setAdditives([]);
      setCustomAdditive('');
      setIndication('');
      setStartDate(formatIsoDateForInput(now));
      setStartTime(formatTimeForInput(now));
    }
    setErrors({});
  }, [show, editFluid]);

  const resolvedFluidType = fluidType === 'Other' ? customFluidType.trim() : fluidType;

  const resolvedRate = rateMode === 'rate'
    ? Number(rateMlPerHour) || null
    : computeRate(volumeMl, durationHours);

  const resolvedDuration = rateMode === 'duration'
    ? Number(durationHours) || null
    : computeDuration(volumeMl, rateMlPerHour);

  const showCalculation = volumeMl && (rateMlPerHour || durationHours);

  const toggleAdditive = (additive) => {
    setAdditives((current) => (current.includes(additive)
      ? current.filter((a) => a !== additive)
      : [...current, additive]));
  };

  const addCustomAdditive = () => {
    const trimmed = customAdditive.trim();
    if (trimmed && !additives.includes(trimmed)) {
      setAdditives((current) => [...current, trimmed]);
    }
    setCustomAdditive('');
  };

  const validate = () => {
    const next = {};
    if (!resolvedFluidType) next.fluidType = 'Fluid type is required.';
    if (!volumeMl || Number(volumeMl) <= 0) next.volumeMl = 'Volume is required.';
    if (rateMode === 'rate' && (!rateMlPerHour || Number(rateMlPerHour) <= 0)) next.rate = 'Rate is required.';
    if (rateMode === 'duration' && (!durationHours || Number(durationHours) <= 0)) next.rate = 'Duration is required.';
    if (!indication.trim()) next.indication = 'Indication is required.';
    if (!startDate || !startTime) next.startDate = 'Start date and time are required.';
    return next;
  };

  const handleSave = () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const finalRate = resolvedRate;
    const finalDuration = resolvedDuration;
    const endDateTime = finalDuration
      ? new Date(startDateTime.getTime() + finalDuration * 60 * 60 * 1000)
      : null;

    const fluid = {
      ...(editFluid || {}),
      id: editFluid?.id || createFluidId(),
      prescriptionType: 'fluid',
      drug: resolvedFluidType,
      volumeMl: Number(volumeMl),
      rateMlPerHour: finalRate,
      durationHours: finalDuration,
      additives,
      route: 'IV',
      indication: indication.trim(),
      start_date: startDateTime.toISOString(),
      end_date: endDateTime ? endDateTime.toISOString() : null,
      status: editFluid?.status || 'active',
      prescriber: editFluid?.prescriber || prescriberName,
      pharmacistApprovalStatus: editFluid?.pharmacistApprovalStatus || { approved: false },
      amendmentHistory: editFluid?.amendmentHistory || [],
    };

    onSave(fluid);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton className="blue-back text-white">
        <Modal.Title>{editFluid ? 'Amend fluid prescription' : 'Prescribe IV fluid'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={fluidType === 'Other' ? 6 : 12}>
            <Form.Group controlId="fluidType">
              <Form.Label>Fluid type <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={fluidType}
                onChange={(e) => { setFluidType(e.target.value); setErrors((c) => ({ ...c, fluidType: '' })); }}
                isInvalid={Boolean(errors.fluidType)}
              >
                <option value="">Select fluid...</option>
                {FLUID_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                <option value="Other">Other (specify)</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.fluidType}</Form.Control.Feedback>
            </Form.Group>
          </Col>

          {fluidType === 'Other' && (
            <Col md={6}>
              <Form.Group controlId="customFluidType">
                <Form.Label>Specify fluid <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={customFluidType}
                  onChange={(e) => { setCustomFluidType(e.target.value); setErrors((c) => ({ ...c, fluidType: '' })); }}
                  placeholder="Enter fluid name"
                  isInvalid={Boolean(errors.fluidType)}
                />
                <Form.Control.Feedback type="invalid">{errors.fluidType}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          )}

          <Col md={4}>
            <Form.Group controlId="volumeMl">
              <Form.Label>Volume (mL) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                value={volumeMl}
                min="1"
                list="fluid-volume-options"
                onChange={(e) => { setVolumeMl(e.target.value); setErrors((c) => ({ ...c, volumeMl: '' })); }}
                placeholder="e.g. 1000"
                isInvalid={Boolean(errors.volumeMl)}
              />
              <datalist id="fluid-volume-options">
                <option value="50" />
                <option value="100" />
                <option value="250" />
                <option value="500" />
                <option value="1000" />
              </datalist>
              <Form.Control.Feedback type="invalid">{errors.volumeMl}</Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="rateMode">
              <Form.Label>Prescribe by</Form.Label>
              <Form.Select value={rateMode} onChange={(e) => { setRateMode(e.target.value); setErrors((c) => ({ ...c, rate: '' })); }}>
                <option value="rate">Rate (mL/hr)</option>
                <option value="duration">Duration (hours)</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            {rateMode === 'rate' ? (
              <Form.Group controlId="rateMlPerHour">
                <Form.Label>Rate (mL/hr) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={rateMlPerHour}
                  min="1"
                  list="fluid-rate-options"
                  onChange={(e) => { setRateMlPerHour(e.target.value); setErrors((c) => ({ ...c, rate: '' })); }}
                  placeholder="e.g. 125"
                  isInvalid={Boolean(errors.rate)}
                />
                <datalist id="fluid-rate-options">
                  <option value="30" />
                  <option value="60" />
                  <option value="83" />
                  <option value="100" />
                  <option value="125" />
                  <option value="167" />
                  <option value="250" />
                  <option value="500" />
                </datalist>
                <Form.Control.Feedback type="invalid">{errors.rate}</Form.Control.Feedback>
              </Form.Group>
            ) : (
              <Form.Group controlId="durationHours">
                <Form.Label>Duration (hours) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={durationHours}
                  min="0.5"
                  step="0.5"
                  list="fluid-duration-options"
                  onChange={(e) => { setDurationHours(e.target.value); setErrors((c) => ({ ...c, rate: '' })); }}
                  placeholder="e.g. 8"
                  isInvalid={Boolean(errors.rate)}
                />
                <datalist id="fluid-duration-options">
                  <option value="1" />
                  <option value="2" />
                  <option value="4" />
                  <option value="6" />
                  <option value="8" />
                  <option value="12" />
                  <option value="24" />
                </datalist>
                <Form.Control.Feedback type="invalid">{errors.rate}</Form.Control.Feedback>
              </Form.Group>
            )}
          </Col>

          {showCalculation && (resolvedRate || resolvedDuration) && (
            <Col md={12}>
              <div className="alert alert-info py-2 mb-0 small d-flex flex-wrap gap-3">
                {resolvedRate && <span><strong>Rate:</strong> {resolvedRate} mL/hr</span>}
                {resolvedDuration && <span><strong>Duration:</strong> {resolvedDuration} hours</span>}
                {startDate && startTime && resolvedDuration && (() => {
                  const start = new Date(`${startDate}T${startTime}`);
                  if (Number.isNaN(start.getTime())) return null;
                  const end = new Date(start.getTime() + resolvedDuration * 3600000);
                  return (
                    <span>
                      <strong>Est. completion:</strong>{' '}
                      {end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      {' '}({end.toLocaleDateString('en-GB')})
                    </span>
                  );
                })()}
              </div>
            </Col>
          )}

          <Col md={12}>
            <Form.Group>
              <Form.Label>Additives</Form.Label>
              <div className="d-flex flex-wrap gap-3 mb-2">
                {ADDITIVE_OPTIONS.map((a) => (
                  <Form.Check
                    key={a}
                    type="checkbox"
                    id={`additive-${a}`}
                    label={a}
                    checked={additives.includes(a)}
                    onChange={() => toggleAdditive(a)}
                  />
                ))}
              </div>
              {additives.filter((a) => !ADDITIVE_OPTIONS.includes(a)).map((a) => (
                <div key={a} className="d-flex align-items-center gap-2 mb-1">
                  <span className="badge bg-secondary">{a}</span>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setAdditives((current) => current.filter((item) => item !== a))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="d-flex gap-2 mt-2">
                <Form.Control
                  type="text"
                  value={customAdditive}
                  onChange={(e) => setCustomAdditive(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomAdditive(); } }}
                  placeholder="Other additive (e.g. Insulin 8 units)"
                  size="sm"
                />
                <Button size="sm" variant="outline-secondary" onClick={addCustomAdditive} disabled={!customAdditive.trim()}>
                  Add
                </Button>
              </div>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group controlId="fluidIndication">
              <Form.Label>Indication <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={indication}
                onChange={(e) => { setIndication(e.target.value); setErrors((c) => ({ ...c, indication: '' })); }}
                placeholder="e.g. Fluid resuscitation, maintenance fluids, hypokalaemia"
                isInvalid={Boolean(errors.indication)}
              />
              <Form.Control.Feedback type="invalid">{errors.indication}</Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="fluidStartDate">
              <Form.Label>Start date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setErrors((c) => ({ ...c, startDate: '' })); }}
                isInvalid={Boolean(errors.startDate)}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="fluidStartTime">
              <Form.Label>Start time <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="time"
                value={startTime}
                onChange={(e) => { setStartTime(e.target.value); setErrors((c) => ({ ...c, startDate: '' })); }}
                isInvalid={Boolean(errors.startDate)}
              />
              {errors.startDate && <div className="invalid-feedback d-block">{errors.startDate}</div>}
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave}>
          {editFluid ? 'Save changes' : 'Prescribe fluid'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
